import { useState, useEffect } from "react";
import { PERSOONLIJK, WISSEL_BERICHT, leesActiefTeam } from "@/lib/actiefTeam";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { ArrowLeft, Brain, Trash2, Loader2, Search } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import MobileBottomNav, { BOTTOM_NAV_SPACER } from "../components/MobileBottomNav";
import OogstMelding from "@/components/OogstMelding";
import SuggestedMemories from "../components/SuggestedMemories";
import VoorstelTelling from "../components/VoorstelTelling";
import WeekMeting from "../components/WeekMeting";
import { useTaal } from "@/contexts/TaalContext";
import { herkomstTekst, plaatsTekst, wanneerTekst } from "@/lib/geheugenregel";
import PrullenbakBlok from "../components/PrullenbakBlok";
import LijktDubbel from "../components/LijktDubbel";
import Taalronde from "../components/Taalronde";

/**
 * Everything Novamund learned about you, and the way out.
 *
 * The screen exists before any new kind of remembering does, and that order is
 * deliberate. Memories were already being written by every successful run, and
 * the only way to see or remove one was to open the workspace it belonged to.
 * Learning that ships without a central way to forget is the thing the design
 * said not to build, and it was already half true.
 *
 * A row is a sentence, then where it applies and where it came from, then a
 * bin. Nothing is folded away behind a menu: forgetting has to be as easy to
 * find as the memory itself, or it is a promise rather than a feature.
 *
 * Laid out for the narrow screen first. The sentence takes the width it needs
 * and wraps; the origin sits under it in smaller type. On a phone that reads
 * as a list, on a desktop the same list simply has more room.
 */
/**
 * Wat een ronde van de knop heeft opgeleverd, in een zin.
 *
 * **APART EN GEEXPORTEERD ZODAT HIJ OP UITKOMST TE TOETSEN IS.** Zat dit in de
 * opmaak, dan zou een toets erover een bronscan zijn, en die blijft groen boven
 * code die de zin wel bevat en nooit toont.
 *
 * **DE REDEN VERVANGT DE ZIN NIET, HIJ KOMT ERBIJ**, en dat is het hele punt.
 * Een veeg kan best een gesprek hebben gelezen en daarna zijn gestopt omdat het
 * bakje vol raakte. Zou de reden de zin overschrijven, dan leest dat als "er is
 * niets gebeurd" terwijl er wel degelijk iets is gelezen.
 */
export function describeHarvest(report, taal = "en") {
  const runs = Array.isArray(report?.gedraaid) ? report.gedraaid.length : 0;
  const added = report?.in_het_bakje || 0;
  if (taal === "nl") {
    // NL/EN-batch A2a: dezelfde zin in het Nederlands. De reden komt van de dienst.
    if (runs === 0) return report?.reden || "Er is niets teruggelezen.";
    const wat = runs === 1 ? "een gesprek" : `${runs} gesprekken`;
    const basis =
      added === 0
        ? `${wat[0].toUpperCase()}${wat.slice(1)} teruggelezen. Er kwam niets nieuws uit.`
        : `${wat[0].toUpperCase()}${wat.slice(1)} teruggelezen en ${added === 1 ? "een ding" : `${added} dingen`} in het blok hierboven gezet.`;
    return report?.reden ? `${basis} ${report.reden}` : basis;
  }
  if (runs === 0) return report?.reden || "Nothing was read back.";
  const what = runs === 1 ? "one conversation" : `${runs} conversations`;
  const base =
    added === 0
      ? `Read back ${what}. Nothing new came up.`
      : `Read back ${what} and put ${added === 1 ? "one thing" : `${added} things`} in the block above.`;
  return report?.reden ? `${base} ${report.reden}` : base;
}

/**
 * Wat "alles aannemen" heeft opgeleverd, in een zin.
 *
 * **DE VERDRINGING STAAT ERIN, EN DAT IS FRANKS EIS VAN 24 AUGUSTUS 2026 MET
 * ZOVEEL WOORDEN: stil verdringen mag niet.** Sinds 11 oktober 2026 heeft het
 * geheugen geen maximum meer en verdringt het niets; tot dan hield het hoogstens
 * `memory_limit` regels per plek en liet het de minst gebruikte vallen. Bij een voor
 * een aanvinken zie je die verdringing per keer voorbijkomen; bij tien tegelijk
 * zou zij ongemerkt gebeuren, en dan verdwijnt er iets uit je geheugen zonder
 * dat er ooit iets over is gezegd.
 *
 * Apart en geexporteerd, zodat een toets hierop een GEDRAGSTOETS is en geen
 * bronscan.
 */
export function describeAcceptAll(uitkomst) {
  const aangenomen = (uitkomst?.accepted || []).length;
  const verdrongen = (uitkomst?.dropped || []).length;
  const mislukt = (uitkomst?.failed || []).length;
  if (!aangenomen && !mislukt) return "Nothing was waiting.";

  const delen = [`${aangenomen} remembered`];
  if (verdrongen) {
    delen.push(verdrongen === 1
      ? "1 older line dropped"
      : `${verdrongen} older lines dropped`);
  }
  if (mislukt) {
    delen.push(mislukt === 1
      ? "1 could not be read and stayed"
      : `${mislukt} could not be read and stayed`);
  }
  return `${delen.join(", ")}.`;
}


/**
 * Waar de veeg is, in een zin.
 *
 * **GEEN PERCENTAGE, en dat is Franks eis van 24 augustus 2026: "geen
 * percentage als je dat niet hard kunt maken; een eerlijke stap-teller is
 * genoeg."** Het aantal bronnen ligt vast zodra de poort klaar is, dus "2 of 3"
 * is een meting. Hoe lang bron 2 nog duurt is dat niet, en daar wordt dus niets
 * over gezegd.
 *
 * Apart en geexporteerd, zodat een toets hierop een gedragstoets is.
 */
export function describeProgress(stand) {
  if (!stand) return "";
  if (stand.stap === "reading" && stand.totaal) {
    // De losse vragen (27 okt 2026): korte gesprekken naast elkaar, zie harvest_gate.losse_vragen.
    if (stand.bron === "vragen") {
      return `Reading back your separate questions (${stand.bij} of ${stand.totaal})`;
    }
    const wat = stand.bron === "agent" ? "the jobs you gave your agents" : "conversation";
    return stand.bron === "agent"
      ? `Reading back ${wat} (${stand.bij} of ${stand.totaal})`
      : `Reading ${wat} ${stand.bij} of ${stand.totaal}`;
  }
  return "Looking for something to read back";
}


export default function MemoryPage() {
  const { t, taal } = useTaal();
  const navigate = useNavigate();
  const [memories, setMemories] = useState([]);
  // Glasronde 0.9.244: laat lijkt dubbel en de taalronde opnieuw lezen na een vertaling of keuze.
  const [ververs, setVervers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [confirmAll, setConfirmAll] = useState(false);
  // Whether Novamund may offer things it noticed about you. Off for every
  // account until somebody turns it on, and it governs offering only: asking
  // it outright works either way.
  const [offering, setOffering] = useState(false);
  // Of de oogst hele gesprekken mag teruglezen. EEN EIGEN SCHAKELAAR, los van
  // `offering`: die gaat over de kaart per bericht. Uit tot iemand hem aanzet.
  const [harvesting, setHarvesting] = useState(false);
  // What the harvest thinks it noticed, waiting to be looked at. BLOK 7 phase
  // A. Kept apart from `memories` on purpose: these two lists must never be
  // able to be confused for each other, because one of them is stored and the
  // other one is not.
  const [suggested, setSuggested] = useState([]);
  const [decidingId, setDecidingId] = useState(null);
  // De knop "kijk eens mee", B5. `lookedBack` is wat de laatste ronde opleverde,
  // in een zin. Leeg tot er op de knop is gedrukt: een scherm dat bij het openen
  // al iets meldt, meldt iets over een ronde die niemand heeft gevraagd.
  const [acceptingAll, setAcceptingAll] = useState(false);
  const [looking, setLooking] = useState(false);
  // Waar de veeg is. Leeg zodra hij klaar is; zie `describeProgress`.
  const [progress, setProgress] = useState("");
  const [lookedBack, setLookedBack] = useState("");
  // De oogstbewaking (21 okt): opnieuw gevraagd zodra het bakje verandert, zodat de
  // melding over een vol bakje verdwijnt wanneer je het leegmaakt. Stil bij een fout.
  const [oogstMelding, setOogstMelding] = useState(null);
  const [wachtendMelding, setWachtendMelding] = useState(null);
  useEffect(() => {
    axios.get(`${API}/memory/harvest/bewaking`)
      .then((r) => {
        setOogstMelding(r.data?.melding || null);
        setWachtendMelding(r.data?.wachtend || null);
      }).catch(() => {});
  }, [suggested.length]);

  // K5 (18 okt): het geheugen hoort bij de werkruimte, het team plus persoonlijk.
  // Na een wissel laadt de lijst opnieuw; in een team krijgt persoonlijk een label.
  const [actiefTeam, setActiefTeam] = useState(PERSOONLIJK);
  useEffect(() => {
    leesActiefTeam().then(setActiefTeam).catch(() => {});
    const gewisseld = (e) => {
      setActiefTeam(e.detail ?? PERSOONLIJK);
      axios.get(`${API}/memory`).then((r) => setMemories(r.data)).catch(() => {});
      // K7e (19 okt): ook de open voorstellen horen bij de werkruimte.
      axios.get(`${API}/memory/candidates`)
        .then((r) => setSuggested(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    };
    window.addEventListener(WISSEL_BERICHT, gewisseld);
    return () => window.removeEventListener(WISSEL_BERICHT, gewisseld);
  }, []);

  useEffect(() => {
    axios
      .get(`${API}/memory`)
      .then((r) => setMemories(r.data))
      .catch(() => toast.error(t("Kon niet lezen wat er onthouden is")))
      .finally(() => setLoading(false));
    // No error toast on this one. An older backend has no tray route, and a
    // red message about a feature that is simply not there yet is noise.
    // Requirement 5: this block is quiet or it is absent.
    axios
      .get(`${API}/memory/candidates`)
      .then((r) => setSuggested(Array.isArray(r.data) ? r.data : []))
      .catch(() => {});
    axios
      .get(`${API}/persona`)
      .then((r) => {
        setOffering(!!r.data.memory_enabled);
        setHarvesting(!!r.data.harvest_enabled);
      })
      .catch(() => {});
  }, [t]);

  const setOffer = async (on) => {
    // Optimistic, then corrected if the server disagrees: a switch that waits
    // for a round trip reads as broken on a slow link.
    setOffering(on);
    try {
      await axios.put(`${API}/persona`, { memory_enabled: on });
    } catch {
      setOffering(!on);
      toast.error(t("Kon dat niet wijzigen"));
    }
  };

  /**
   * De oogst aan of uit. **EEN ANDERE SCHAKELAAR DAN DIE HIERBOVEN**, en dat
   * is Franks besluit van 23 augustus 2026. De eerste bestuurt de kaart die
   * met een antwoord meekomt en ermee verdwijnt; deze bestuurt iets dat hele
   * gesprekken teruglest. Een schakelaar voor allebei zou betekenen dat wie de
   * kaart aanzet ongemerkt ook de oogster aanzet.
   */
  const setHarvest = async (on) => {
    setHarvesting(on);
    try {
      await axios.put(`${API}/persona`, { harvest_enabled: on });
    } catch {
      setHarvesting(!on);
      toast.error(t("Kon dat niet wijzigen"));
    }
  };

  /**
   * De knop "kijk eens mee": lees nu terug wat aan de beurt is.
   *
   * **HET OPEN GESPREK IS HIER LEEG, en dat is geen vergeten veld.** Dit scherm
   * is niet een gesprek; er staat er dus geen open. Het veld gaat wel mee zodat
   * dezelfde route straks vanaf een gespreksscherm bruikbaar is zonder dat de
   * server iets hoeft te raden.
   *
   * **GEEN `toast.error` OP EEN WEIGERING.** De server antwoordt met 200 en een
   * reden; die reden hoort onder de knop te staan, want zij legt uit wat er te
   * doen valt. Een rood bericht zou "er is iets kapot" zeggen over een systeem
   * dat precies doet wat het hoort te doen.
   */
  const lookNow = async () => {
    setLooking(true);
    setLookedBack("");
    // **DE TELLER LOOPT NAAST HET VERZOEK, en dat moet ook: de POST blijft
    // minuten open en kan zelf niets tussentijds zeggen.** Elke twee seconden
    // is genoeg voor iets dat per stap een minuut duurt; vaker vragen zou
    // verkeer zijn zonder nieuws.
    const tikker = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/memory/harvest/progress`);
        setProgress(describeProgress(r.data?.progress));
      } catch {
        // Stil: de teller is een extraatje en mag de veeg nooit in de weg zitten.
      }
    }, 2000);
    try {
      const r = await axios.post(`${API}/memory/harvest`, { open_conversation_id: "" });
      const verslag = r.data || {};
      if (Array.isArray(verslag.candidates)) setSuggested(verslag.candidates);
      setLookedBack(describeHarvest(verslag, taal));
    } catch {
      setLookedBack("Could not look back just now. Nothing was read.");
    } finally {
      clearInterval(tikker);
      setProgress("");
      setLooking(false);
    }
  };

  /**
   * Alles aannemen. Franks verzoek van 24 augustus 2026: tien keer klikken is
   * de reden dat het leren stilstaat als hij het vergeet.
   *
   * **DIT BLIJFT TOESTEMMING.** Op deze knop drukken IS zeggen dat het mag; wat
   * er niet komt is automatisch opslaan. Het aantal staat in het opschrift, dus
   * je ziet waar je ja tegen zegt voordat je het doet.
   */
  // **HOEVEEL BESLUITEN ER OP DIT SCHERM ZIJN GENOMEN**, en verder niets. Het
  // getal zelf heeft geen betekenis; het bestaat om het tellingblok te laten
  // ophalen zodra er iets te halen valt.
  //
  // **HIJ GAAT ALLEEN OMHOOG NA EEN GESLAAGD BESLUIT.** Een mislukte poging
  // heeft niets veranderd, dus er valt niets nieuws te tellen; ophogen in een
  // `finally` zou de server juist bevragen op het moment dat er niets gebeurde.
  const [besluiten, setBesluiten] = useState(0);

  const acceptAll = async () => {
    setAcceptingAll(true);
    try {
      const res = await axios.post(`${API}/memory/candidates/accept-all`);
      const uitkomst = res.data || {};
      setSuggested([]);
      setMemories((prev) => [
        ...(uitkomst.accepted || []).map((een) => een.memory), ...prev]);
      // EEN melding en geen tien: bij een voor een is elke regel nieuws, hier is
      // de UITKOMST het nieuws. Wat er is verdrongen staat erin.
      const zin = describeAcceptAll(uitkomst);
      if ((uitkomst.dropped || []).length || (uitkomst.failed || []).length) {
        toast.info(zin);
      } else {
        toast.success(zin);
      }
      setBesluiten((n) => n + 1);
    } catch {
      toast.error(t("Kon die niet aannemen"));
    } finally {
      setAcceptingAll(false);
    }
  };

  /**
   * F1-vervolg (16 oktober 2026): een eigen regel met de organisatie delen, of
   * terugzetten naar alleen jou. De backend (`PUT /memory/{id}/bereik`, F1d)
   * beslist wat deelbaar is en weigert een kijker; daarna leest het scherm de
   * lijst opnieuw, zodat de plaats van de regel uit dezelfde bron komt als
   * altijd.
   */
  const [delenId, setDelenId] = useState(null);
  const zetBereik = async (item, organisatie) => {
    setDelenId(item.id);
    try {
      await axios.put(`${API}/memory/${item.id}/bereik`, { organisatie });
      const r = await axios.get(`${API}/memory`);
      setMemories(r.data);
      toast.success(organisatie ? t("Gedeeld met de organisatie") : t("Weer alleen voor jou"));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : t("Dat lukte niet. Er is niets gewijzigd."));
    } finally {
      setDelenId(null);
    }
  };

  const forget = async (id, text) => {
    try {
      await axios.delete(`${API}/memory/${id}`);
      setMemories((prev) => prev.filter((m) => m.id !== id));
      // Naming what went is the point. "Forgotten" alone leaves you unsure
      // which line you just removed.
      toast.success(`Forgotten: ${text}`);
    } catch {
      toast.error(t("Kon dat niet vergeten"));
    }
  };

  /**
   * Accept one: it becomes a memory and the new line appears in the list
   * below, so the move from "suggested" to "remembered" is something you SEE
   * rather than something you are told about.
   */
  const accept = async (candidate, tekst) => {
    setDecidingId(candidate.id);
    try {
      // TREDE 2: `tekst` weglaten betekent "neem het over zoals het er staat".
      // De dienst bepaalt of het een WIJZIGING is door te vergelijken, dus dit
      // scherm kan de telling niet scheeftrekken door het veld altijd te vullen.
      // ZONDER HERSCHRIJVING GAAT ER GEEN BODY MEE, en dat is geen
      // schoonheid maar de bestaande afspraak: de knop deed dit al zo, en de
      // route leest een ontbrekende body als "neem het over zoals het er
      // staat". Een lege `{}` meesturen zou hetzelfde doen en de aanroep
      // stilzwijgend van vorm veranderen.
      const pad = `${API}/memory/candidates/${candidate.id}/accept`;
      const res = tekst === undefined
        ? await axios.post(pad)
        : await axios.post(pad, { tekst });
      setSuggested((prev) => prev.filter((c) => c.id !== candidate.id));
      setMemories((prev) => [res.data.memory, ...prev]);
      // What had to leave to make room is said out loud, exactly as the
      // remember flow already does. A memory that vanishes without a word is
      // indistinguishable from one that was never stored.
      // `info` en niet `success`: er is iets weggegaan, en dat is nieuws en
      // geen felicitatie. Een kale `toast(...)` bestaat nergens anders in deze
      // codebase, dus die zou hier ook niet horen.
      (res.data.dropped || []).forEach((gone) =>
        toast.info(`Forgotten to make room: ${gone}`));
      toast.success(t("Onthouden"));
      setBesluiten((n) => n + 1);
    } catch {
      toast.error(t("Kon dat niet onthouden"));
    } finally {
      setDecidingId(null);
    }
  };

  /**
   * Dismiss one. **THIS DOES NOT COME BACK**, so the toast says so rather than
   * a bare "dismissed": it is the only moment the promise is visible.
   */
  const dismiss = async (candidate) => {
    setDecidingId(candidate.id);
    try {
      await axios.post(`${API}/memory/candidates/${candidate.id}/dismiss`);
      setSuggested((prev) => prev.filter((c) => c.id !== candidate.id));
      toast.success(t("Weggeklikt, en het wordt niet opnieuw voorgesteld"));
      setBesluiten((n) => n + 1);
    } catch {
      toast.error(t("Kon dat niet wegklikken"));
    } finally {
      setDecidingId(null);
    }
  };

  const forgetEverything = async () => {
    try {
      const res = await axios.delete(`${API}/memory`);
      setMemories([]);
      setConfirmAll(false);
      toast.success(`Forgotten ${res.data.forgotten} things`);
    } catch {
      toast.error(t("Kon niet alles vergeten"));
    }
  };

  return (
    <div className={`h-screen flex flex-col bg-background ${BOTTOM_NAV_SPACER}`}>
      <header className="flex items-center px-4 gap-3 border-b min-h-12 pt-[var(--veilig-boven)]">
        <Button variant="ghost" size="icon" onClick={() => navigate("/")} className="tap-target h-8 w-8">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <h1 className="font-serif text-lg font-light">
          <Brain className="h-4 w-4 inline mr-2 text-[hsl(var(--accent))]" />
          {t("Geheugen")}
        </h1>
        {/* Glasronde 0.9.243 punt 2: de prullenbak staat altijd in de kop, ook als hij leeg is. */}
        <PrullenbakBlok soorten={["geheugen"]} teller={memories.length} className="ml-auto"
                        onTerug={() => axios.get(`${API}/memory`).then((r) => setMemories(r.data)).catch(() => {})} />
        {memories.length > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setConfirmAll(true)}
            data-testid="forget-all-btn"
          >
            {t("Alles vergeten")}
          </Button>
        )}
      </header>

      <div className="flex-1 overflow-y-auto p-4">
        {/* ON TOP, and that is the requirement and not a layout preference.
            Build plan of 29 July, section 1.5: "Het Memory-scherm krijgt er een
            blok boven". A tray you have to scroll to is a tray that fills up.
            It renders nothing at all when empty, so on a quiet day this costs
            the screen no space. */}
        {/* Glasronde 0.9.244: regels in een andere taal (punt 2) en regels die dubbel lijken (B), bovenaan. */}
        <Taalronde teller={memories.length + ververs}
                   onKlaar={() => { setVervers((v) => v + 1); axios.get(`${API}/memory`).then((r) => setMemories(r.data)).catch(() => {}); }} />
        <LijktDubbel regels={memories} teller={memories.length + ververs}
                     onGekozen={() => axios.get(`${API}/memory`).then((r) => setMemories(r.data)).catch(() => {})} />
        <OogstMelding melding={oogstMelding} wachtend={wachtendMelding} />
        <SuggestedMemories
          candidates={suggested}
          onAccept={accept}
          onDismiss={dismiss}
          onAcceptAll={acceptAll}
          acceptingAll={acceptingAll}
          busyId={decidingId}
        />

        {/* HOE DE VOORSTELLEN GINGEN, en dat is de meting waar trede 3 aan
            hangt (MASTERPLAN 3ax-4). Hij staat BOVEN de lijst en niet eronder:
            Franks eis van 25 augustus 2026, gemeten in zijn eigen antwoorden -
            een regel onderaan begint op 63 tot 83 procent van de tekst en
            wordt niet gelezen. */}
        <VoorstelTelling prikkel={besluiten} />

        <p className="mb-4 max-w-prose text-sm text-muted-foreground">
          {t("Wat Novamund heeft geleerd en wat je het kunt laten vergeten. Het staat alleen op deze machine, versleuteld, en niets hiervan gaat de deur uit.")}
        </p>

        <div className="mb-4 max-w-prose rounded-lg border p-3">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm">{t("Voorstellen wat hij over je opmerkt")}</span>
            {/* The hit area is grown with an invisible box rather than by
                drawing a bigger switch, same as the web access one. */}
            <Switch
              checked={offering}
              onCheckedChange={setOffer}
              aria-label={t("Voorstellen wat te onthouden")}
              data-testid="memory-offer-switch"
              className="relative flex-shrink-0 after:absolute after:-inset-3 after:content-['']"
            />
          </div>
          <p className="mt-2 text-xs text-muted-foreground">
            {t("Staat dit aan, dan wordt een voorkeur die je terloops noemt aan je voorgelegd om aan te nemen of te negeren. Er wordt nooit iets over je bewaard zonder dat je het zegt. Zeggen \u201conthoud dat ...\u201d werkt hoe dan ook, want dan vraag jij het.")}
          </p>
        </div>

        {/* DE TWEEDE SCHAKELAAR, en dat hij apart is, is de eis. Franks besluit
            van 23 augustus 2026. De schakelaar hierboven gaat over de kaart die
            met een antwoord meekomt en ermee verdwijnt; deze gaat over iets dat
            hele gesprekken teruglest. Een schakelaar voor allebei zou betekenen
            dat wie de kaart aanzet ongemerkt ook de oogster aanzet, en dat zijn
            twee heel verschillende dingen om ja tegen te zeggen. */}
        <div className="mb-4 max-w-prose rounded-lg border p-3">
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm">{t("Afgeronde gesprekken teruglezen")}</span>
            <Switch
              checked={harvesting}
              onCheckedChange={setHarvest}
              aria-label={t("Afgeronde gesprekken teruglezen")}
              data-testid="memory-harvest-switch"
              className="relative flex-shrink-0 after:absolute after:-inset-3 after:content-['']"
            />
          </div>
          <p className="mt-2 text-xs text-muted-foreground" data-testid="memory-harvest-uitleg">
            {t("Staat dit aan, dan wordt een afgerond gesprek een keer teruggelezen en komt wat steeds terugkomt in het blok Voorgesteld hierboven, met de citaten waarop het rust. Er zijn twee losse keren nodig voordat hij iets voorstelt, en hij stelt er per keer hooguit drie voor. Wat wacht blijft staan en verloopt niet, hoeveel het er ook zijn; zijn het er meer dan tien, dan meldt deze pagina bovenaan hoeveel. Er wordt niets bewaard tot jij het aanneemt, en wat je wegklikt komt nooit terug.")}
          </p>

          {/* DE KNOP STAAT ER ALLEEN ALS DE SCHAKELAAR AAN IS. Een knop die
              netjes uitlegt dat de functie uitstaat terwijl de schakelaar er
              vlak boven staat, is een omweg naar iets dat al zichtbaar is. De
              route weigert hem hoe dan ook; dit is de vorm, niet de grens. */}
          {harvesting && (
            <div className="mt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={lookNow}
                disabled={looking}
                data-testid="memory-harvest-now"
              >
                {looking ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Search className="mr-2 h-4 w-4" />
                )}
                {t("Nu terugkijken")}
              </Button>
              {/* DE TELLER STAAT WAAR DE UITSLAG STRAKS KOMT, en niet ergens
                  anders: dan schuift het scherm niet op het moment dat de veeg
                  klaar is. */}
              {looking && progress && (
                <p
                  className="mt-2 text-xs text-muted-foreground"
                  data-testid="memory-harvest-progress"
                >
                  {progress}
                </p>
              )}
              {!looking && lookedBack && (
                <p
                  className="mt-2 text-xs text-muted-foreground"
                  data-testid="memory-harvest-result"
                >
                  {lookedBack}
                </p>
              )}
            </div>
          )}
          {/* ZL5 (besluit ZL5-1): de weekmeting hangt aan deze schakelaar, dus
              haar trend staat hier en alleen als de schakelaar aan is. */}
          {harvesting && <WeekMeting />}
        </div>

        {/* Besluit Frank 11 oktober 2026: geen maximum meer, dus geen regelaar.
            Per vraag gaan alleen de regels mee die erbij horen. */}
        <p className="mb-4 max-w-prose text-xs text-muted-foreground"
           data-testid="memory-no-limit">
          {t("Het geheugen heeft geen maximum. Er verdwijnt nooit een regel om ruimte te maken; bij elke vraag gaan alleen de regels mee die erbij horen.")}
        </p>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t("Lezen")}
          </div>
        )}

        {!loading && memories.length === 0 && (
          <p className="text-sm text-muted-foreground" data-testid="memory-empty">
            {t("Er is nog niets onthouden.")}
          </p>
        )}

        {!loading && memories.length > 0 && (
          <ul className="space-y-2" data-testid="memory-list">
            {memories.map((item) => (
              <li
                key={item.id}
                className="flex items-start gap-2 rounded-lg border p-3"
                data-testid={`memory-${item.id}`}
              >
                {/* min-w-0, or a long sentence pushes the bin off the row. */}
                <div className="min-w-0 flex-1">
                  <p className="break-words text-sm">{item.text}</p>
                  {/* Glasronde 0.9.244: de letterlijke woorden of de originele tekst waar deze regel uit komt. */}
                  {item.citaat && (
                    <p className="mt-1 break-words text-xs italic text-muted-foreground" data-testid={`citaat-${item.id}`}>
                      {t("Oorspronkelijk:")} &ldquo;{item.citaat}&rdquo;
                    </p>
                  )}
                  {/* Glasronde 0.9.245 F: regels die automatisch met deze zijn samengevoegd. */}
                  {(item.citaten || []).map((c, i) => (
                    <p key={i} className="mt-1 break-words text-xs italic text-muted-foreground" data-testid={`samengevoegd-${item.id}`}>
                      {t("Samengevoegd met:")} &ldquo;{c}&rdquo;
                    </p>
                  ))}
                  <p className="mt-1 text-xs text-muted-foreground">
                    {plaatsTekst(item, t)}
                    {/* Een herkende herkomst is zelf een zin ("je vroeg me het te
                        onthouden"); alleen een vrije tekst krijgt "van" ervoor. */}
                    {item.herkomst
                      ? ` · ${herkomstTekst(item, t, taal)}`
                      : item.source ? ` · ${t("van {bron}", { bron: item.source })}` : ""}
                    {` · ${wanneerTekst(item, t, taal)}`}
                  </p>
                  {actiefTeam !== PERSOONLIJK && item.persoonlijk && !item.van_een_ander && (
                    <p className="mt-1 text-xs text-muted-foreground" data-testid={`persoonlijk-${item.id}`}>
                      {t("Persoonlijk")}
                    </p>
                  )}
                  {item.van_een_ander && (
                    <p className="mt-1 text-xs text-[hsl(var(--accent))]" data-testid={`van-een-ander-${item.id}`}>
                      {t("Gedeeld door een ander lid")}
                    </p>
                  )}
                  {/* Alleen een eigen regel die overal geldt of al gedeeld is:
                      een regel over een project verliest anders zijn plek
                      (de backend weigert die ook). */}
                  {!item.van_een_ander && (item.scope === "global" || item.scope === "organisatie") && (
                    <button
                      type="button"
                      className="mt-1 text-xs underline text-muted-foreground disabled:opacity-50"
                      disabled={delenId === item.id}
                      onClick={() => zetBereik(item, item.scope !== "organisatie")}
                      data-testid={`deel-${item.id}`}
                    >
                      {item.scope === "organisatie" ? t("Terugzetten") : t("Delen met organisatie")}
                    </button>
                  )}
                </div>
                {/* Een regel van een ander lid kan alleen die ander vergeten
                    (de backend geeft hier 404); een prullenbak die niets doet,
                    staat er dus niet. */}
                {!item.van_een_ander && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className="tap-target h-8 w-8 shrink-0"
                    onClick={() => forget(item.id, item.text)}
                    aria-label={`Forget: ${item.text}`}
                    data-testid={`forget-${item.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      <Dialog open={confirmAll} onOpenChange={setConfirmAll}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("Alles vergeten?")}</DialogTitle>
          </DialogHeader>
          {/* A confirmation that says what disappears. "Are you sure" tells
              you nothing you did not already know. */}
          <p className="text-sm text-muted-foreground" data-testid="forget-all-warning">
            {t("Dit verwijdert alle {n} dingen die Novamund heeft geleerd, in elk project en elke werkruimte. Dat is niet terug te draaien.", { n: memories.length })}
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmAll(false)}>
              {t("Laat staan")}
            </Button>
            <Button variant="destructive" onClick={forgetEverything} data-testid="forget-all-confirm">
              {t("Alles vergeten")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <MobileBottomNav />
    </div>
  );
}
